/**
 * WealthImplementationConcepts — seven life-stage cards × five earner tiers.
 *
 * 35 total strategies (7 buckets × 5 tiers). Each card shows the age band,
 * the life-stage tagline, and a tier dropdown that streams the 3-min audio
 * playbook for that specific (age × tier) combination.
 *
 * Voice: calm-sharp-story-driven with a little swagger.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api, API_BASE } from "../lib/api";
import { ArrowRight, Play, Pause, Spinner, Sparkle, Buildings, Compass } from "@phosphor-icons/react";

const TIER_ORDER = ["t1", "t2", "t3", "t4", "t5"];
const TIER_META = {
  t1: { label: "Working Wealth",   range: "$40K – $150K" },
  t2: { label: "Rising Pro",       range: "$180K – $250K" },
  t3: { label: "Established Pro",  range: "$280K – $450K" },
  t4: { label: "Executive Class",  range: "$500K – $750K" },
  t5: { label: "Capital Owner",    range: "$780K+" },
};

function StrategyCard({ bucket, activeKey, setActiveKey }) {
  const [selectedTier, setSelectedTier] = useState("t1");
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);

  // Stop our audio if a different card became active
  useEffect(() => {
    const ourKey = `${bucket.id}::${selectedTier}`;
    if (activeKey !== ourKey && audioRef.current) {
      audioRef.current.pause();
      setState((s) => (s === "playing" ? "paused" : s));
    }
  }, [activeKey, bucket.id, selectedTier]);

  // Reset audio when the tier changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setState("idle");
  }, [selectedTier]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  const togglePlay = () => {
    const key = `${bucket.id}::${selectedTier}`;
    if (state === "playing" && audioRef.current) {
      audioRef.current.pause();
      setState("paused");
      return;
    }
    if (state === "paused" && audioRef.current) {
      audioRef.current
        .play()
        .then(() => {
          setState("playing");
          setActiveKey(key);
        })
        .catch(() => setState("idle"));
      return;
    }
    setState("loading");
    setActiveKey(key);
    const url = `${API_BASE}/wealth-implementation/${bucket.id}/${selectedTier}/audio`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onplaying = () => setState("playing");
    audio.onpause = () => setState((s) => (s === "playing" ? "paused" : s));
    audio.onended = () => setState("idle");
    audio.onerror = () => setState("idle");
    audio.play().catch(() => setState("idle"));
  };

  const tierData = (bucket.tiers || {})[selectedTier];
  const playerLabel =
    state === "loading" ? "Writing your script…"
    : state === "playing" ? "Pause"
    : state === "paused" ? "Resume"
    : "Play 3-min Strategy";

  return (
    <article
      data-testid={`impl-card-${bucket.id}`}
      className="card-surface p-7 lg:p-8 flex flex-col h-full"
    >
      <p className="eyebrow text-xs">{bucket.life_stage}</p>
      <h2 className="font-display text-4xl lg:text-5xl mt-2 leading-none gold-text">
        {bucket.label}
      </h2>
      <p className="text-sm text-exodus-muted mt-3 italic leading-relaxed">
        {bucket.tagline}
      </p>

      {/* TIER SELECTOR */}
      <div className="mt-6 border-t border-exodus-border pt-5">
        <p className="text-[10px] eyebrow tracking-[2px] text-exodus-muted mb-3">Earner tier</p>
        <div className="grid grid-cols-5 gap-1">
          {TIER_ORDER.map((id) => {
            const t = TIER_META[id];
            const active = id === selectedTier;
            return (
              <button
                key={id}
                onClick={() => setSelectedTier(id)}
                data-testid={`impl-tier-${bucket.id}-${id}`}
                className={`px-1.5 py-2 text-center transition-all border ${
                  active
                    ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
                    : "border-exodus-border text-exodus-muted hover:border-exodus-gold/60 hover:text-exodus-text"
                }`}
                title={`${t.label} · ${t.range}`}
              >
                <p className="font-mono text-[10px] uppercase">{id}</p>
              </button>
            );
          })}
        </div>
        {tierData && (
          <div className="mt-3 space-y-1">
            <p className="font-display text-base text-exodus-text">{TIER_META[selectedTier].label}</p>
            <p className="text-[11px] text-exodus-gold font-mono">{tierData.income_range || TIER_META[selectedTier].range}</p>
            {tierData.typical_jobs && (
              <p className="text-[11px] text-exodus-muted leading-relaxed">
                {tierData.typical_jobs.join(" · ")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* PLAY */}
      <button
        data-testid={`impl-play-${bucket.id}`}
        onClick={togglePlay}
        className={`mt-5 inline-flex items-center gap-2 px-4 py-3 border transition-all w-full justify-center ${
          state === "playing"
            ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
            : "border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg"
        }`}
      >
        {state === "loading" ? (
          <Spinner size={16} className="animate-spin" />
        ) : state === "playing" ? (
          <Pause size={16} weight="fill" />
        ) : (
          <Play size={16} weight="fill" />
        )}
        <span className="text-[10px] eyebrow tracking-[3px]">{playerLabel}</span>
      </button>
      {state === "loading" && (
        <p className="text-[10px] text-exodus-muted mt-2 italic">
          First listen takes 30-45 seconds. Replays are instant.
        </p>
      )}
    </article>
  );
}

export default function WealthImplementation() {
  const [buckets, setBuckets] = useState([]);
  const [activeKey, setActiveKey] = useState(null);

  useEffect(() => {
    api
      .get("/wealth-implementation")
      .then((r) => setBuckets(r.data.buckets || []))
      .catch(() => setBuckets([]));
  }, []);

  return (
    <div data-testid="wealth-implementation" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-12">
        <p className="eyebrow"><span className="gold-divider" />Wealth Implementation Concepts</p>
        <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95] max-w-4xl">
          How to beat the system,{" "}
          <span className="gold-text italic">at any age, in any bracket.</span>
        </h1>
        <p className="text-lg text-exodus-muted mt-8 max-w-2xl leading-relaxed">
          Seven life stages. Five earner tiers. Thirty-five 3-minute strategies
          that show you exactly how to use structured products to live tax-free,
          borrow against your own capital, and pass wealth on your terms.
        </p>

        {/* TIER LEGEND */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {TIER_ORDER.map((id) => {
            const t = TIER_META[id];
            return (
              <div key={id} className="card-surface px-4 py-3" data-testid={`tier-legend-${id}`}>
                <p className="font-mono text-[10px] uppercase text-exodus-muted">{id}</p>
                <p className="font-display text-sm mt-1 text-exodus-text">{t.label}</p>
                <p className="text-[11px] text-exodus-gold font-mono mt-1">{t.range}</p>
              </div>
            );
          })}
        </div>

        {/* WHY THIS PAGE */}
        <div className="mt-12 grid sm:grid-cols-3 gap-4">
          <div className="card-surface p-5">
            <Compass size={20} weight="thin" className="text-exodus-gold" />
            <p className="font-display text-lg mt-3 leading-tight">Tax-free retirement</p>
            <p className="text-xs text-exodus-muted mt-2 leading-relaxed">
              How policy loans give you uncapped tax-free income with no age
              gate, no R M D, and no 1099 at withdrawal.
            </p>
          </div>
          <div className="card-surface p-5">
            <Sparkle size={20} weight="thin" className="text-exodus-gold" />
            <p className="font-display text-lg mt-3 leading-tight">Living benefits</p>
            <p className="text-xs text-exodus-muted mt-2 leading-relaxed">
              Borrow against your own capital for business, real estate,
              kids&apos; college, or a chronic-illness rider that pays before death.
            </p>
          </div>
          <div className="card-surface p-5">
            <Buildings size={20} weight="thin" className="text-exodus-gold" />
            <p className="font-display text-lg mt-3 leading-tight">Beat the bracket</p>
            <p className="text-xs text-exodus-muted mt-2 leading-relaxed">
              The structured-product playbook the I R S doesn&apos;t advertise
              and the average advisor doesn&apos;t sell.
            </p>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {buckets.length === 0 ? (
            <p className="text-exodus-muted col-span-full">Loading playbooks…</p>
          ) : (
            buckets.map((b) => (
              <StrategyCard key={b.id} bucket={b} activeKey={activeKey} setActiveKey={setActiveKey} />
            ))
          )}
        </div>
      </section>

      {/* SOFT CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20 border-t border-exodus-border">
        <p className="eyebrow">Ready to make this your plan?</p>
        <h3 className="font-display text-3xl lg:text-4xl mt-3 leading-tight">
          Reserve a 30-minute private session with Leon.
        </h3>
        <p className="text-exodus-muted mt-4 leading-relaxed max-w-xl">
          One hundred dollar refundable deposit. You&apos;ll walk away with a written
          assessment built around the strategies you just listened to.
        </p>
        <div className="flex flex-wrap gap-4 mt-8">
          <Link to="/reserve" className="btn-gold" data-testid="impl-reserve">
            Reserve a Session <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/calculator" className="btn-ghost" data-testid="impl-calculator">
            Run Your Numbers
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
